import React, { useEffect, useState, useRef, useMemo } from "react";
import CFSelect from "../shared/forms/CFSelect";
import useIntegrationRefresh from "../integrations/useIntegrationRefresh";
import { buildTypedFromParent } from "../../utils/integrationFieldTypeMap";

const ConditionSettingsInput = (props) => {
  const [selectSettingValue, setSelectSettingValue] = useState(undefined);
  const hiddenInputRef = useRef(null);
  const textInputRef = useRef(null);
  const { isIntegrationRefreshing } = useIntegrationRefresh();

  const setting = props.setting;
  const integration = props.integration;
  const website = props.website;
  const integrationLoading = isIntegrationRefreshing(
    integration && integration.id
  );
  const parsedSetting = setting.attribute
    ? setting.attribute.replace("[", "-").replace("]", "")
    : "";
  const inputIdSuffix =
    props.inputIdSuffix || (props.dropdownOnly ? "inline" : "");
  const textInputId = `condition-${props.condition?.id}-${parsedSetting}${
    inputIdSuffix ? `-${inputIdSuffix}` : ""
  }`;

  let setting_value;
  let value_paths = setting.attribute
    ? setting.attribute
        .replace("][", ".")
        .replace("[", "")
        .replace("]", "")
        .split(".")
    : [];

  value_paths.forEach((path) => {
    if (setting_value) {
      setting_value = setting_value[path];
    } else if (props.condition && integration) {
      setting_value = props.condition["data"] && props.condition["data"][path];
    } else if (props.condition) {
      setting_value = props.condition[path];
    }
  });

  const updateInputValue = () => {
    if (setting.input_type == "select") {
      if (setting.multi_select == true && setting_value) {
        // Handle multi-select: convert comma-separated string to array of option objects
        const options = settingSelectOptions();
        const values = setting_value
          .split(/\s*,\s*/)
          .filter((v) => v.length > 0);
        const selectedOptions = values
          .map((value) => {
            const trimmedValue = value.trim();
            return options.find((option) => option.value == trimmedValue);
          })
          .filter((option) => option !== undefined);

        if (selectedOptions.length > 0) {
          setSelectSettingValue(selectedOptions);
        } else if (setting_value) {
          // Fallback: create option objects from values if not found in options
          setSelectSettingValue(
            values.map((value) => ({
              label: value.trim(),
              value: value.trim(),
            }))
          );
        }
      } else {
        // Single select
        let selectOption = settingSelectOptions().find(
          (option) => option.value == setting_value
        );

        if (selectOption || setting_value) {
          setSelectSettingValue(
            selectOption || {
              label: setting_value,
              value: setting_value,
            }
          );
        }
      }
    }

    if (setting.input_type !== "select") {
      if (
        textInputRef.current &&
        document.activeElement !== textInputRef.current
      )
        textInputRef.current.value = setting_value || setting.value || "";
    }
  };

  useEffect(() => {
    updateInputValue();
  }, [props.condition, setting_value]);

  // Update hidden input value when selectSettingValue changes (for multi-select)
  useEffect(() => {
    if (hiddenInputRef.current && setting.input_type == "select") {
      const value =
        setting.multi_select == true && Array.isArray(selectSettingValue)
          ? selectSettingValue.map((option) => option.value).join(", ")
          : selectSettingValue?.value || "";
      hiddenInputRef.current.value = value;
      // Also update the value attribute for form submission
      hiddenInputRef.current.setAttribute("value", value);
    }
  }, [selectSettingValue, setting.input_type, setting.multi_select]);

  const changeCallback = (selectedOption) => {
    if (
      setting.input_type == "select" &&
      setting.child_setting &&
      setting.child_setting.reset_on_parent_change == true &&
      props.formData &&
      props.setFormData
    ) {
      const newFormData = _.cloneDeep(props.formData);
      _.set(
        newFormData,
        integration
          ? `[data][${setting.child_setting.attribute}]`
          : setting.child_setting.attribute,
        ""
      );
      props.setFormData(newFormData);
    }

    if (setting.input_type == "select") {
      if (setting.multi_select == true) {
        // Handle multi-select: convert array to comma-separated string
        setSelectSettingValue(selectedOption || []);

        // Create a synthetic event object with comma-separated value
        const value = selectedOption
          ? selectedOption.map((option) => option.value).join(", ")
          : "";
        const syntheticEvent = {
          value: value,
          label: selectedOption
            ? selectedOption.map((option) => option.label).join(", ")
            : "",
        };

        if (props.onChange) {
          props.onChange(syntheticEvent);
        }

        // Call onDone for multi-select to trigger save
        if (props.onDone) {
          props.onDone(syntheticEvent);
        }
      } else {
        // Single select
        setSelectSettingValue(selectedOption || { label: "", value: "" });

        if (props.onChange) {
          props.onChange(selectedOption || { label: "", value: "" });
        }
      }
    } else {
      setSelectSettingValue(selectedOption.target.value);

      if (props.onChange) {
        props.onChange(selectedOption);
      }
    }

    if (
      props.onDone &&
      setting.input_type == "select" &&
      setting.multi_select != true
    ) {
      props.onDone(selectedOption || { label: "", value: "" });
    }

    if (setting.callback) {
      setting.callback(selectedOption);
    }
  };

  const settingSelectOptions = () => {
    let selectOptions = [
      {
        value: "",
        label: setting.placeholder,
        isDisabled: true,
      },
    ];

    if (
      integration &&
      integration.integration_type == "Shopify" &&
      setting.dropdown == "products" &&
      (!integration.api_data[setting.dropdown] ||
        Object.values(integration.api_data[setting.dropdown]).length == 0)
    ) {
      const populateProducts = (products) => {
        products.forEach((product) => {
          selectOptions.push({
            value: product.handle,
            label: product.title,
          });
        });
      };

      if (!window.cf_shopify_products) {
        new ProductsIntegrationsService("Shopify", integration)
          .adapter()
          .getProducts()
          .then((response) => {
            window.cf_shopify_products = response;
            populateProducts(response);
          });
      } else {
        populateProducts(window.cf_shopify_products);
      }
    }

    if (
      integration &&
      integration.integration_type == "Shopify" &&
      setting.dropdown == "product_variants" &&
      (!integration.api_data[setting.dropdown] ||
        Object.values(integration.api_data[setting.dropdown]).length == 0)
    ) {
      const populateProducts = (products) => {
        const productsVariants = products
          .map((product) => product.variants)
          .flat();
        productsVariants.forEach((variant) => {
          selectOptions.push({
            value: variant.id,
            label: variant.variant_title,
          });
        });
      };

      if (!window.cf_shopify_products) {
        new ProductsIntegrationsService("Shopify", integration)
          .adapter()
          .getProducts()
          .then((response) => {
            window.cf_shopify_products = response;
            populateProducts(response);
          });
      } else {
        populateProducts(window.cf_shopify_products);
      }
    }

    {
      integration &&
        integration.api_data[setting.dropdown] &&
        Object.values(integration.api_data[setting.dropdown]).forEach(
          (option) => {
            selectOptions.push({
              value: setting.dropdown_id_key
                ? option[setting.dropdown_id_key]
                : option.id,
              label: option.name,
            });
          }
        );
    }

    {
      !integration &&
        setting.dropdown == "fields" &&
        Object.values(website.fields)
          .filter((f) => f.service == "ConvertFlow")
          .forEach((field) => {
            if (
              selectOptions.filter((o) => o.value == field.slug).length == 0
            ) {
              selectOptions.push({
                value: field.slug,
                label: field.slug,
              });
            }
          });
    }

    {
      !integration &&
        setting.dropdown == "tags" &&
        Object.values(website.tags).forEach((tag) => {
          selectOptions.push({
            value: tag.id,
            label: tag.name,
          });
        });
    }

    {
      !integration &&
        setting.options &&
        setting.options
          .filter((option) => {
            return (
              option &&
              option.text &&
              (!option.parent || option.parent == props.parent_value?.value)
            );
          })
          .sort(function (a, b) {
            return a.text.localeCompare(b.text);
          })
          .forEach((dropdown_option, i) => {
            var custom_attributes = {};

            dropdown_option.custom_attributes &&
              dropdown_option.custom_attributes.forEach((custom_attribute) => {
                custom_attributes[custom_attribute.name] =
                  custom_attribute.value;
              });

            selectOptions.push({
              value: dropdown_option.value,
              label: dropdown_option.text,
              ...custom_attributes,
            });
          });
    }

    return selectOptions;
  };

  const typedHiddenInputName = integration
    ? `condition[data][${setting.attribute}]`
    : `condition${setting.attribute}`;

  const parentProperty = useMemo(() => {
    if (!setting.typed_from_parent) return null;
    const properties =
      integration &&
      integration.api_data &&
      (integration.api_data.properties || integration.api_data.fields);
    const parentVal = props.parent_value && props.parent_value.value;
    if (!properties || !parentVal) return null;
    return Object.values(properties).find(
      (p) => String(p.id) === String(parentVal)
    );
  }, [
    setting.typed_from_parent,
    integration,
    props.parent_value && props.parent_value.value,
  ]);

  const typedFromParent = useMemo(
    () => buildTypedFromParent(parentProperty, setting_value),
    [parentProperty, setting_value]
  );

  const typedSingleValue = useMemo(() => {
    if (!typedFromParent || typedFromParent.kind !== "select") return undefined;
    if (
      selectSettingValue &&
      !Array.isArray(selectSettingValue) &&
      selectSettingValue.value
    ) {
      return selectSettingValue;
    }
    if (
      setting_value === undefined ||
      setting_value === null ||
      setting_value === ""
    )
      return undefined;
    return (
      typedFromParent.options.find(
        (o) => String(o.value) === String(setting_value)
      ) || {
        value: setting_value,
        label: setting_value,
      }
    );
  }, [typedFromParent, setting_value, selectSettingValue]);

  const typedMultiValue = useMemo(() => {
    if (!typedFromParent || typedFromParent.kind !== "multi_select") return [];
    if (Array.isArray(selectSettingValue)) {
      return selectSettingValue;
    }
    if (!setting_value) return [];
    return String(setting_value)
      .split(", ")
      .filter((v) => v.length > 0)
      .map(
        (v) =>
          typedFromParent.options.find((o) => o.value === v) || {
            value: v,
            label: v,
          }
      );
  }, [typedFromParent, setting_value, selectSettingValue]);

  const typedHiddenValue = () => {
    if (!typedFromParent) return "";
    if (typedFromParent.kind === "multi_select") {
      return Array.isArray(typedMultiValue)
        ? typedMultiValue.map((o) => o.value).join(", ")
        : "";
    }
    return (typedSingleValue && typedSingleValue.value) || "";
  };

  const typedSelectChange = (selected) => {
    const event = { label: selected.label, value: selected.value };
    setSelectSettingValue(event);
    if (props.onChange) props.onChange(event);
    if (props.onDone) props.onDone(event);
  };

  const typedMultiChange = (selected) => {
    const arr = Array.isArray(selected) ? selected : [];
    const stringValue = arr.map((o) => o.value).join(", ");
    const event = { value: stringValue, label: stringValue };
    setSelectSettingValue(arr);
    if (props.onChange) props.onChange(event);
    if (props.onDone) props.onDone(event);
  };

  const settingTitle = (() => {
    if (typeof selectSettingValue === "string") {
      return selectSettingValue;
    }
    if (setting.multi_select == true && Array.isArray(selectSettingValue)) {
      return selectSettingValue
        .map((option) => option.label || option.value)
        .filter(Boolean)
        .join(", ");
    }
    if (selectSettingValue && !Array.isArray(selectSettingValue)) {
      return selectSettingValue.label || selectSettingValue.value || "";
    }
    if (typedSingleValue) {
      return typedSingleValue.label || typedSingleValue.value || "";
    }
    if (Array.isArray(typedMultiValue) && typedMultiValue.length > 0) {
      return typedMultiValue
        .map((option) => option.label || option.value)
        .filter(Boolean)
        .join(", ");
    }
    return setting_value == null ? "" : String(setting_value);
  })();

  return (
    <React.Fragment>
      <div
        className={`${
          props.hideLabel || props.dropdownOnly
            ? "w-full min-w-0"
            : "flex-grow min-w-[100px]"
        } ${setting.note ? "pb-[10px]" : ""}`}
        title={settingTitle || undefined}
        style={{
          display: setting.input_type == "hidden" ? "none" : "",
        }}
      >
        {typedFromParent && typedFromParent.kind === "select" && (
          <React.Fragment>
            <CFSelect
              className="smart-select"
              placeholder={setting.placeholder}
              isSearchable={true}
              menuPortalTarget={document.body}
              required="required"
              name={typedHiddenInputName}
              options={typedFromParent.options}
              value={typedSingleValue}
              onChange={typedSelectChange}
              isLoading={integrationLoading}
            />
            <input
              ref={hiddenInputRef}
              name={typedHiddenInputName}
              tabIndex={-1}
              autoComplete="off"
              style={{ display: "none" }}
              data-input-type="hidden"
              value={typedHiddenValue()}
              required="required"
              readOnly
            />
          </React.Fragment>
        )}

        {typedFromParent && typedFromParent.kind === "multi_select" && (
          <React.Fragment>
            <CFSelect
              className="smart-select"
              placeholder={setting.placeholder}
              isSearchable={true}
              isMulti={true}
              menuPortalTarget={document.body}
              required="required"
              name={typedHiddenInputName}
              options={typedFromParent.options}
              value={typedMultiValue}
              onChange={typedMultiChange}
              isLoading={integrationLoading}
            />
            <input
              ref={hiddenInputRef}
              name={typedHiddenInputName}
              tabIndex={-1}
              autoComplete="off"
              style={{ display: "none" }}
              data-input-type="hidden"
              value={typedHiddenValue()}
              required="required"
              readOnly
            />
          </React.Fragment>
        )}

        {typedFromParent && typedFromParent.kind === "native" && (
          <input
            className="w-full p-3 bg-white dark:bg-slate-950 text-xl text-black dark:text-white rounded-xl border-solid border-[1px] border-slate-200 dark:border-slate-700 focus:border-slate-200 outline-none focus:outline-none focus:ring-0 appearance-none"
            placeholder={setting.placeholder}
            type={typedFromParent.inputType}
            name={typedHiddenInputName}
            required="required"
            min={setting.min}
            defaultValue={setting_value || setting.value}
            title={settingTitle || undefined}
            onChange={changeCallback}
            onBlur={props.onDone}
          ></input>
        )}

        {!typedFromParent && setting.input_type !== "select" && (
          <input
            ref={textInputRef}
            id={textInputId}
            className="w-full p-3 bg-white dark:bg-slate-950 text-xl text-black dark:text-white rounded-xl border-solid border-[1px] border-slate-200 dark:border-slate-700 focus:border-slate-200 outline-none focus:outline-none focus:ring-0 appearance-none"
            placeholder={setting.placeholder}
            type={setting.input_type}
            name={
              integration
                ? `condition[data][${setting.attribute}]`
                : `condition${setting.attribute}`
            }
            required="required"
            min={setting.min}
            defaultValue={setting_value || setting.value}
            title={settingTitle || undefined}
            data-input-type={setting.input_type}
            onChange={changeCallback}
            onBlur={props.onDone}
          ></input>
        )}

        {!typedFromParent && setting.input_type == "select" && (
          <React.Fragment>
            <CFSelect
              className="smart-select"
              isSearchable={true}
              menuPortalTarget={
                props.dropdownOnly ||
                props.hideLabel ||
                props.layout === "stacked"
                  ? document.body
                  : undefined
              }
              menuPortalZIndex={props.menuPortalZIndex}
              isMulti={setting.multi_select == true}
              name={
                integration
                  ? `condition[data][${setting.attribute}]`
                  : `condition${setting.attribute}`
              }
              options={settingSelectOptions()}
              value={selectSettingValue}
              onChange={changeCallback}
              isLoading={integrationLoading}
            />

            <input
              ref={hiddenInputRef}
              name={
                integration
                  ? `condition[data][${setting.attribute}]`
                  : `condition${setting.attribute}`
              }
              tabIndex={-1}
              autoComplete="off"
              style={{ display: "none" }}
              data-input-type="hidden"
              value={
                setting.multi_select == true &&
                Array.isArray(selectSettingValue)
                  ? selectSettingValue.map((option) => option.value).join(", ")
                  : selectSettingValue?.value || ""
              }
              required="required"
            />
          </React.Fragment>
        )}
      </div>

      {setting.input_type == "select" &&
        setting.child_setting &&
        !props.dropdownOnly && (
          <ConditionSettingsInput
            condition={props.condition}
            integration={props.integration}
            setting={setting.child_setting}
            parent_value={selectSettingValue}
            onChange={(e) => {
              let newFormData = props.formData;
              _.set(
                newFormData,
                `${
                  !integration
                    ? setting.child_setting.attribute
                    : `[data][${setting.child_setting.attribute}]`
                }`,
                e.value || e.target.value
              );
              props.setFormData(newFormData);

              if (
                props.condition &&
                !props.builder &&
                setting.child_setting.input_type == "select"
              ) {
                props.handleSubmit(e, newFormData);
              }
            }}
            onDone={(e) => {
              if (props.condition) {
                if (props.builder) {
                  const value = e.value || e.target.value;
                  const updates = [
                    {
                      object_type: "conditions",
                      object_id: props.condition.id,
                      setting_name: !integration
                        ? setting.child_setting.attribute
                        : `[data][${setting.child_setting.attribute}]`,
                      value: value,
                    },
                  ];

                  if (!integration && e.field_option_id !== undefined) {
                    updates.push({
                      object_type: "conditions",
                      object_id: props.condition.id,
                      setting_name: "[data][field_option_id]",
                      value: e.field_option_id + "",
                    });
                  }

                  dispatchCustomEvent("updateBuilder", { updates });
                } else if (setting.child_setting.input_type !== "select") {
                  props.handleSubmit(e);
                }
              }
            }}
          />
        )}
    </React.Fragment>
  );
};

export default ConditionSettingsInput;
